import './Button.css'

import { motion } from 'framer-motion'
import { useRef } from 'react'

import clockBig from '../../Resources/clockBig.gif'
import { Icon } from './Icon'
import { Text } from './Text'

const clickButtonVariants = (e) => ({
  idle: { x: 0, y: 0, scale: 1 },
  expanded: { scale: 1, x: 0, y: 0, width: e }
})

const clickButtonNoShadowVariants = (e) => ({
  idle: { x: 0, y: 0, scale: 1 },
  expanded: { scale: 1, x: 0, y: 0, width: e }
})

const ImgSpinner = () => <img alt="loading" src={clockBig} className="nbtn-motion__spinner-img" />

/**
 * Costruisce le classi CSS del bottone animato in base alle props visive.
 * @param {Object} opts - Opzioni di stile del bottone
 * @param {boolean} opts.disabled - Stato disabilitato
 * @param {boolean} opts.inverted - Variante invertita
 * @param {boolean} opts.round - Forma circolare
 * @param {boolean} opts.label - Presenza di etichetta testuale
 * @param {boolean} opts.noShadow - Disabilita ombra
 * @param {string} [opts.className] - Classe aggiuntiva
 * @returns {string} Stringa classi CSS
 */
const buildButtonClassName = ({ disabled, inverted, round, label, noShadow, className }) =>
  [
    'nbtn-motion',
    className || 'nbtn',
    disabled && 'is-disabled',
    inverted && 'nbtn-motion--inverted',
    round && 'nbtn-motion--round',
    !round && label && 'nbtn-motion--padded',
    label && 'nbtn-motion--labeled',
    !noShadow && 'nbtn-motion--shadow'
  ]
    .filter(Boolean)
    .join(' ')

export const Button = ({
  round,
  iconSize,
  disabled,
  hidden,
  spinner,
  icon,
  label,
  onClick,
  style,
  iconStyle,
  width,
  tooltip,
  inverted,
  labelProps = {},
  noShadow,
  styleText,
  allowMultipleClick,
  onHover,
  className
}) => {
  const tooltipRef = useRef(null)

  if (hidden) return null
  if (spinner) return <ImgSpinner />

  const expansion = tooltipRef.current?.offsetWidth ? tooltipRef.current?.offsetWidth + 30 : 'auto'
  const variants = noShadow ? clickButtonNoShadowVariants : clickButtonVariants

  const doNothing = () => null

  const onClickFunction = (e) => {
    // Evita il doppio click
    if (e?.detail !== 1 && !allowMultipleClick) doNothing()
    else onClick(e)
  }

  return (
    <motion.div
      whileHover={round && tooltip && 'expanded'}
      onClick={!disabled ? onClickFunction : undefined}
      animate="idle"
      transition={{ delay: 0.3, duration: 0.5 }}
      style={{ width: width || 'auto', ...style }}
      className={buildButtonClassName({ disabled, inverted, round, label, noShadow, className })}
      variants={variants(expansion)}
      onHoverStart={() => (onHover ? onHover() : null)}
    >
      {icon ? (
        <Icon
          name={icon}
          size={iconSize || 18}
          iconStyle={{
            marginTop: -1,
            ...(round ? { marginLeft: 6.5 } : {}),
            ...iconStyle
          }}
          color={inverted ? 'white' : 'inherit'}
        />
      ) : null}
      {label && !round ? (
        <Text
          color={inverted ? 'white' : 'inherit'}
          bold
          center
          value={label}
          className={[
            'nbtn-motion__label',
            icon && 'nbtn-motion__label--with-icon',
            width && 'nbtn-motion__label--grow'
          ]
            .filter(Boolean)
            .join(' ')}
          style={styleText}
        />
      ) : null}
      {tooltip ? (
        <div ref={tooltipRef}>
          <Text
            className="nbtn-motion__tooltip"
            bold
            value={tooltip}
            color={inverted ? 'white' : 'inherit'}
            {...labelProps}
          />
        </div>
      ) : null}
    </motion.div>
  )
}
